import React, { useState, useEffect, useRef } from 'react';
// Usaremos datos mock para asegurar la funcionalidad en este entorno de archivo único.
// En un entorno de desarrollo real, esta línea importaría la configuración de Firebase:
// import { collection, onSnapshot, query, orderBy, db } from 'firebase/firestore'; 
import {
  Box,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Divider,
  keyframes // Importar keyframes para definir la animación del sintillo
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CasinoIcon from '@mui/icons-material/Casino';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';

// 🎰 PALETA CASINO — COLORES ACTUALIZADOS PARA TEMA CLARO Y VIBRANTE
const CASINO = {
  // Fondo
  BG: '#FFFFFF', // Blanco puro (Fondo principal)
  
  // ✅ LETRAS (optimizadas para legibilidad y estilo en fondo claro)
  TITLE: '#E74C3C',      // Rojo vibrante — títulos principales
  CODE: '#0080FF',        // Azul brillante — códigos únicos, números grandes
  SUBTITLE: '#34495E',    // Azul grisáceo oscuro — subtítulos, etiquetas
  TEXT: '#333333',        // Gris oscuro (Texto principal para contraste)
  META: '#7F8C8D',        // Gris medio — fechas, metadatos
  ALERT: '#FFEA00',      // Amarillo Neón (Marquee y acento)
  
  // Elementos UI
  CARD_BG: 'rgba(255, 255, 255, 0.95)', // Fondo de tarjeta blanco casi puro
  BORDER_VIP: '#FF8C00', // Naranja/Dorado fuerte para VIP
  // Sombra VIP mucho más intensa y ancha
  SHADOW_VIP: '0 0 35px rgba(255, 140, 0, 0.6)', 
  SHADOW_DEFAULT: '0 5px 15px rgba(0,0,0,0.2)', // Sombra sutil para tema claro
};

// ---------------------------
// ANIMACIÓN CSS: Marquee (Sintillo)
// ---------------------------
const marquee = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

// ---------------------------
// Componente Auxiliar: StatRow
// ---------------------------
const StatRow = ({ label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: CASINO.SUBTITLE, fontSize: '1rem', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight="bold" sx={{ color, fontSize: '1.8rem', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
    {/* Separador sutil */}
    <Divider sx={{ mt: 1, borderColor: `${CASINO.META}40` }} /> 
  </Box>
);

// ---------------------------
// Componente Auxiliar: StatisticsPanel
// ---------------------------
const StatisticsPanel = ({ totalUsers, totalTickets, soloUsers }) => {
  const averageTickets = totalUsers > 0 ? (totalTickets / totalUsers).toFixed(2) : '0.00';

  return (
    <Paper
      elevation={10}
      sx={{
        p: 4, 
        borderRadius: '20px', 
        // Fondo de tarjeta claro
        background: CASINO.CARD_BG,
        border: `3px solid ${CASINO.CODE}`,
        boxShadow: `0 0 25px ${CASINO.CODE}40`, // Sombra de color
        height: '100%',
        minHeight: { xs: 300, md: 500 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="h4"
        fontWeight={900}
        sx={{ 
          fontSize: { xs: '2rem', md: '3rem' }, 
          textAlign: 'center', 
          color: CASINO.CODE, 
          mb: 4,
          textShadow: `0 0 10px ${CASINO.CODE}40`,
          letterSpacing: '2px'
        }}
      >
        <BarChartIcon sx={{ fontSize: '1.2em', verticalAlign: 'middle', mr: 1 }} />
        RESUMEN DEL CASINO
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* totalUsers ahora usa CASINO.TEXT (gris oscuro) para buen contraste */}
        <StatRow label="PARTICIPANTES ACTIVOS" value={totalUsers} color={CASINO.TEXT} />
        {/* totalTickets usa color de título (Rojo vibrante) */}
        <StatRow label="BOLETOS VENDIDOS TOTALES" value={totalTickets} color={CASINO.TITLE} />
        {/* Promedio usa color de alerta (Amarillo neón) */}
        <StatRow
          label="PROMEDIO DE BOLETOS POR JUGADOR"
          value={averageTickets}
          color={CASINO.ALERT}
        />
        {/* Solo users usa color META */}
        <StatRow 
          label="JUGADORES CON 1 BOLETO (¡Última Oportunidad!)" 
          value={soloUsers.length} 
          color={CASINO.META} 
        />
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography sx={{ color: CASINO.SUBTITLE, fontWeight: 700, fontSize: '1.1rem' }}>
            🎉 ¡Cada boleto tiene una oportunidad igual de ganar!
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};


// ---------------------------
// Configuración y Lógica de Rifa
// ---------------------------
const TARGET_DATE = new Date('2025-12-22T21:30:00-04:00'); // Fecha de Sorteo

const calculateTimeLeft = () => {
  const diff = TARGET_DATE.getTime() - Date.now();
  return diff > 0
    ? {
        days: Math.floor(diff / 864e5),
        hours: Math.floor((diff / 36e5) % 24),
        minutes: Math.floor((diff / 6e4) % 60),
        seconds: Math.floor((diff / 1e3) % 60),
      }
    : { days: 0, hours: 0, minutes: 0, seconds: 0 };
};

// ---------------------------
// MOCK DATABASE / DUMMY IMPLEMENTATION (Para que el código sea ejecutable)
// ---------------------------
const mockParticipantsData = [
    { username: 'ElPatron007', tickets: 25, latest: Date.now() - 5000 },
    { username: 'LadyLuck', tickets: 18, latest: Date.now() - 10000 },
    { username: 'KingSlot', tickets: 10, latest: Date.now() - 15000 },
    { username: 'NeoMatrix', tickets: 9, latest: Date.now() - 20000 },
    { username: 'LanaReyes', tickets: 5, latest: Date.now() - 25000 },
    { username: 'JuanSolo', tickets: 3, latest: Date.now() - 30000 },
    { username: 'PacoTickets', tickets: 2, latest: Date.now() - 35000 },
    { username: 'ZoeGamer', tickets: 1, latest: Date.now() - 40000 },
    { username: 'Alpha2024', tickets: 1, latest: Date.now() - 45000 },
    { username: 'BetaOmega', tickets: 1, latest: Date.now() - 50000 },
    { username: 'GigaChad', tickets: 1, latest: Date.now() - 55000 },
    { username: 'MegaMind', tickets: 1, latest: Date.now() - 60000 },
    { username: 'Usuario1', tickets: 1, latest: Date.now() - 65000 },
    { username: 'Usuario2', tickets: 1, latest: Date.now() - 70000 },
    { username: 'Apolo11', tickets: 4, latest: Date.now() - 75000 },
    { username: 'RayoMcQueen', tickets: 7, latest: Date.now() - 80000 },
    { username: 'PrincesaPeach', tickets: 2, latest: Date.now() - 85000 },
    { username: 'SuperMario', tickets: 6, latest: Date.now() - 90000 },
    { username: 'MasterChief', tickets: 1, latest: Date.now() - 95000 },
    { username: 'CortanaAI', tickets: 1, latest: Date.now() - 100000 },
].sort((a, b) => b.tickets - a.tickets);

const mockGroupedData = (data) => {
    const totalTickets = data.reduce((sum, p) => sum + p.tickets, 0);
    const totalUsers = data.length;
    return { list: data, totalTickets, totalUsers };
};

const useMockData = (setParticipants, setTotalTickets, setTotalUsers, setLoading) => {
    useEffect(() => {
        setLoading(true);
        // Simula un pequeño retraso de carga
        setTimeout(() => {
            try {
                const { list, totalTickets, totalUsers } = mockGroupedData(mockParticipantsData);
                setParticipants(list);
                setTotalTickets(totalTickets);
                setTotalUsers(totalUsers);
            } catch (e) {
                console.error('Error al cargar datos mock.', e);
            } finally {
                setLoading(false);
            }
        }, 1000);
    }, [setParticipants, setTotalTickets, setTotalUsers, setLoading]);
};

// ---------------------------
// Componente Principal: MoniToRifa
// ---------------------------
function MoniToRifa() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [totalTickets, setTotalTickets] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // Página 0 es ahora la página de estadísticas
  const rafRef = useRef();
  
  // Usar hook de mock data (Descomentar y reemplazar por lógica de Firebase para producción)
  useMockData(setParticipants, setTotalTickets, setTotalUsers, setLoading);

  // ⏱️ Contador preciso
  useEffect(() => {
    const tick = () => {
      setTimeLeft(calculateTimeLeft());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);
  
  // 📡 Lógica de datos en tiempo real (COMENTAR/REMOVER useMockData al descomentar esto)
  /*
  useEffect(() => {
     setLoading(true);
     // ... Lógica de Firestore
     // ...
     return () => unsubscribe();
  }, []);
  */

  // 🎰 Slider automático (cada 8s)
  useEffect(() => {
    // La paginación real comienza después de la página 0 (Estadísticas)
    const totalContentPages = Math.ceil(participants.length / 9);
    const maxPage = totalContentPages; // TotalPages incluye la página 0

    if (maxPage <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % (maxPage + 1));
    }, 8000); // 8 segundos
    
    return () => clearInterval(interval);
  }, [participants.length]);

  const soloUsers = participants.filter((p) => p.tickets === 1);
  // Total de páginas = 1 (Estadísticas) + Math.ceil(Jugadores / 9)
  const totalPages = 1 + Math.ceil(participants.length / 9); 
  
  // El índice de inicio real para los participantes se calcula restando 1 a la página actual
  const contentPage = currentPage - 1;
  const startIndex = contentPage * 9;
  const currentGroup = participants.slice(startIndex, startIndex + 9);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          background: CASINO.BG,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Loading spinner en color de título para contraste */}
        <CircularProgress size={80} sx={{ color: CASINO.TITLE }} />
      </Box>
    );
  }

  // Define el contenido del cuerpo del slider (Estadísticas o Jugadores)
  const sliderContent = currentPage === 0 ? (
    <StatisticsPanel 
      totalUsers={totalUsers} 
      totalTickets={totalTickets} 
      soloUsers={soloUsers} 
    />
  ) : (
    <Grid container spacing={3}>
      {currentGroup.map((p, i) => {
        const pos = startIndex + i + 1;
        const isTop3 = pos <= 3;
        const isVIP = p.tickets >= 5;

        return (
          <Grid item xs={12} sm={6} md={4} key={`${p.username}-${pos}`}>
            <Paper
              elevation={10}
              sx={{
                minHeight: 180,
                borderRadius: '20px', 
                // Fondo de tarjeta claro
                background: CASINO.CARD_BG,
                border: isVIP ? `3px solid ${CASINO.BORDER_VIP}` : `1px solid ${CASINO.META}80`,
                boxShadow: isVIP ? CASINO.SHADOW_VIP : CASINO.SHADOW_DEFAULT,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s ease',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: isVIP ? CASINO.SHADOW_VIP : '0 15px 35px rgba(0,0,0,0.3)',
                },
                p: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                {/* Posición Emblema */}
                <Box
                  sx={{
                    width: 65,
                    height: 65,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    fontSize: '2.2rem',
                    color: '#000', // Texto oscuro en fondo brillante
                    background: isTop3
                      ? pos === 1 ? CASINO.BORDER_VIP : pos === 2 ? '#C0C0C0' : '#CD7F32'
                      : CASINO.TITLE, // Usamos TITLE (Rojo) para el resto
                    border: `4px solid ${CASINO.TEXT}`,
                    boxShadow: isTop3 ? `0 0 15px ${CASINO.BORDER_VIP}` : 'none',
                  }}
                >
                  {isTop3 ? ['🥇', '🥈', '🥉'][pos - 1] : pos}
                </Box>

                {/* Nombre y VIP Tag */}
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    noWrap
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.8rem',
                      color: CASINO.TEXT, // Texto oscuro en fondo claro
                    }}
                  >
                    {p.username}
                  </Typography>
                  {isVIP && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                        px: 1.5,
                        py: 0.4,
                        borderRadius: '8px',
                        background: CASINO.BORDER_VIP, // Naranja vibrante para VIP
                        color: CASINO.CARD_BG, // Fondo de tarjeta (Blanco)
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        textShadow: '0 0 5px rgba(0,0,0,0.5)'
                      }}
                    >
                      🌟 VIP PLAYER
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Cantidad de boletos — Brillo Máximo */}
              <Box sx={{ textAlign: 'center', mt: 'auto' }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    color: CASINO.CODE, // Azul brillante
                    fontSize: '5rem', 
                    lineHeight: 1,
                    textShadow: `0 0 15px ${CASINO.CODE}80`,
                  }}
                >
                  {p.tickets}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: CASINO.SUBTITLE, 
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    mt: 0.5,
                    textTransform: 'uppercase'
                  }}
                >
                  BOLETO{p.tickets !== 1 ? 'S' : ''}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: '100vw',
        minHeight: '100vh',
        background: CASINO.BG, // Fondo blanco/claro
        color: CASINO.TEXT, // Texto principal oscuro
        position: 'relative',
        overflowX: 'hidden', 
        py: 3, 
        px: { xs: 1, md: 4 },
        
        // Efecto de patrón de puntos eliminado para un fondo limpio.
        // Se puede añadir un patrón muy sutil si se desea.
        /*
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          pointerEvents: 'none',
          backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.1) 1px, transparent 1px)',
          backgroundSize: '10px 10px',
          zIndex: -1,
        }
        */
      }}
    >
      {/* 1. SECCIÓN SUPERIOR: Título y Contador */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        {/* Título - Brillo Extremo */}
        <Typography
          variant="h1"
          sx={{
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: { xs: '6px', sm: '12px' },
            color: CASINO.TITLE, // Rojo vibrante
            textShadow: `
              0 0 10px rgba(231, 76, 60, 0.8), 
              0 0 20px rgba(231, 76, 60, 0.5)
            `,
            fontSize: { xs: '3rem', md: '5rem' },
            lineHeight: 1,
            mb: 1,
            mt: 2
          }}
        >
          <CasinoIcon sx={{ fontSize: '1em', verticalAlign: 'middle', mr: 1 }} />
          TUS 100K DE NAVIDAD
          <CasinoIcon sx={{ fontSize: '1em', verticalAlign: 'middle', ml: 1 }} />
        </Typography>

        {/* Contador - Estilo de Panel Digital/Vault */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap', mb: 4 }}>
          {['days', 'hours', 'minutes', 'seconds'].map((unit) => (
            <Box key={unit} sx={{ textAlign: 'center' }}>
              <Paper
                elevation={10}
                sx={{
                  width: { xs: 85, sm: 110 },
                  height: { xs: 85, sm: 110 },
                  borderRadius: '16px',
                  // Fondo claro para el contador
                  background: 'linear-gradient(145deg, #F9F9F9, #E0E0E0)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `3px solid ${CASINO.CODE}80`,
                  boxShadow: `0 0 20px ${CASINO.CODE}40`,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: CASINO.CODE, // Azul brillante
                    fontSize: { xs: '2.5rem', sm: '3.5rem' },
                    textShadow: `0 0 10px ${CASINO.CODE}80`,
                    lineHeight: 1,
                  }}
                >
                  {String(timeLeft[unit]).padStart(2, '0')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: CASINO.SUBTITLE, 
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.7rem', sm: '0.9rem' },
                    mt: 0.5,
                    fontWeight: 600
                  }}
                >
                  {unit}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontSize: '1.5rem',
            color: CASINO.SUBTITLE,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: '1.2em', verticalAlign: 'bottom', mr: 1 }} />
          ¡TIEMPO RESTANTE PARA EL SORTEO!
        </Typography>
      </Box>

      {/* 2. SECCIÓN CENTRAL: SLIDER (Estadísticas y Jugadores) */}
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ width: 8, height: 32, backgroundColor: CASINO.TITLE, borderRadius: '4px', mr: 2, boxShadow: '0 0 10px rgba(231, 76, 60, 0.8)' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '2rem', color: CASINO.TITLE }}>
            {currentPage === 0 ? '📊 RESUMEN GENERAL' : `🏆 TOP PARTICIPANTES (Pág ${currentPage} de ${totalPages - 1})`}
          </Typography>
          
          {/* Controles de paginación */}
          {totalPages > 1 && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                size="large"
                onClick={() => setCurrentPage((p) => (p === 0 ? totalPages - 1 : p - 1))}
                sx={{ color: CASINO.TITLE, '&:hover': { color: CASINO.CODE } }}
              >
                <ArrowBackIcon fontSize="large" />
              </IconButton>
              <Typography sx={{ color: CASINO.SUBTITLE, fontSize: '1.2rem', fontWeight: 'bold' }}>
                {currentPage + 1} / {totalPages}
              </Typography>
              <IconButton
                size="large"
                onClick={() => setCurrentPage((p) => (p + 1) % totalPages)}
                sx={{ color: CASINO.TITLE, '&:hover': { color: CASINO.CODE } }}
              >
                <ArrowForwardIcon fontSize="large" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Contenido del Slider (Estadísticas o Jugadores) */}
        <Box sx={{ minHeight: { xs: 400, md: 550 }, transition: 'all 0.9s ease' }}>
          {sliderContent}
        </Box>
        
        {/* Indicadores de página */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 4 }}>
            {[...Array(totalPages)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: i === currentPage ? CASINO.TITLE : CASINO.META,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: i === currentPage ? '2px solid white' : 'none',
                  boxShadow: i === currentPage ? '0 0 8px rgba(231, 76, 60, 0.8)' : 'none',
                }}
                onClick={() => setCurrentPage(i)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* 3. SINTILLO (MARQUEE) - Siempre en la parte inferior */}
      {soloUsers.length > 0 && (
        <Box
          sx={{
            position: 'fixed', 
            bottom: 0,
            left: 0,
            width: '100%',
            height: { xs: 70, md: 96 },
            background: CASINO.ALERT, // Fondo Amarillo Neón
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            zIndex: 100, 
            boxShadow: '0 -10px 30px rgba(0,0,0,0.3)',
            borderTop: `5px solid ${CASINO.TITLE}` // Borde Rojo
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              whiteSpace: 'nowrap',
              animation: `${marquee} 70s linear infinite`, // VELOCIDAD LENTA (70s)
              '&:hover': { animationPlayState: 'paused' },
              px: 6,
            }}
          >
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '1.8rem', md: '2.6rem' },
                color: CASINO.TEXT, // Texto Gris Oscuro para contraste máximo
                textShadow: '0 2px 4px rgba(255,255,255,0.4)',
                letterSpacing: '2px',
                lineHeight: 1,
              }}
            >
              🎟️ ¡ÚLTIMA CHANCE! JUGADORES CON 1 BOLETO: {soloUsers.map((u) => u.username.toUpperCase()).join(' • ')} •{' '}
              {soloUsers.map((u) => u.username.toUpperCase()).join(' • ')} • ¡COMPRA TU BOLETO AHORA! 💥
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Mensaje de error (si existe) */}
      {error && (
        <Alert severity="error" variant="filled" sx={{ mt: 3, fontSize: '1.2rem', backgroundColor: CASINO.TITLE }}>
          {error}
        </Alert>
      )}
    </Container>
  );
}

export default MoniToRifa;